/**
 * EmbedOnlyApp — the ENTIRE app when OfficeHub is framed by another site on
 * /forms/embed/<slug> (see CrossOriginFrameGuard, App.tsx).
 *
 * WHY: the public website is same-site with app.onpointgen.com, so a staff member's
 * session is live inside its iframe. Rendering the normal app tree there would mount
 * AuthProvider/Organization, module prefetch, usage tracking, offline
 * sync (SyncBadge with Retry-all/Discard), the command palette and ModuleLayout's
 * navigation (module selector, sign-out) inside a page the website controls.
 *
 * WHAT IT MOUNTS: only the embed form route, under signed-OUT auth and no-org
 * contexts (the public-form fields call useAuth/useOrganization and handle null),
 * plus a Toaster for the form's own messages. QueryClientProvider and the top-level
 * ErrorBoundary stay OUTSIDE the guard in App.tsx and still apply. EmbedFormPage
 * brings its own PublicStorageContext, reCAPTCHA provider and error boundary.
 */
import { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AnonymousAuthProvider } from '../../contexts/AuthContext';
import { AnonymousOrganizationProvider } from '../../contexts/OrganizationContext';
import { CardSkeleton } from '../../components/shared';
import { lazyWithRetry } from '../lazyWithRetry';

const EmbedFormPage = lazyWithRetry(() => import('../../pages/Forms/EmbedFormPage'));

export function EmbedOnlyApp() {
  return (
    <AnonymousAuthProvider>
      <AnonymousOrganizationProvider>
        <Toaster position="top-center" />
        <Suspense fallback={<CardSkeleton />}>
          <Routes>
            <Route path="/forms/embed/:slug" element={<EmbedFormPage />} />
            <Route path="*" element={null} />
          </Routes>
        </Suspense>
      </AnonymousOrganizationProvider>
    </AnonymousAuthProvider>
  );
}
